import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, User } from '../api/api';
import { initializeNotifications } from '../utils/notifications';

interface AuthContextType {
  user: User | null;
  token: string | null;
  originalAdmin: User | null;
  login: (email: string, password: string) => Promise<void>;
  register: (username: string, email: string, password: string) => Promise<any>;
  logout: () => void;
  logoutFromImpersonation: () => Promise<void>;
  impersonateUser: (userId: number) => Promise<void>;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isManager: boolean;
  isImpersonating: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [originalAdmin, setOriginalAdmin] = useState<User | null>(null);

  useEffect(() => {
    try {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      const storedOriginalAdmin = localStorage.getItem('originalAdmin');
      if (storedToken && storedUser) {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
        if (storedOriginalAdmin) {
          setOriginalAdmin(JSON.parse(storedOriginalAdmin));
        }
        // Логируем в Electron для отладки
        if (typeof window !== 'undefined' && window.electronAPI) {
          console.log('Токен загружен из localStorage в Electron:', storedToken.substring(0, 20) + '...');
        }
        
        // Инициализируем push-уведомления если пользователь уже авторизован
        initializeNotifications().catch(err => 
          console.error('Ошибка инициализации уведомлений:', err)
        );
      } else {
        // Пробуем sessionStorage как fallback
        const sessionToken = sessionStorage.getItem('token');
        const sessionUser = sessionStorage.getItem('user');
        if (sessionToken && sessionUser) {
          setToken(sessionToken);
          setUser(JSON.parse(sessionUser));
          // Переносим в localStorage
          try {
            localStorage.setItem('token', sessionToken);
            localStorage.setItem('user', sessionUser);
          } catch (e) {
            console.error('Не удалось перенести токен из sessionStorage в localStorage:', e);
          }
          
          // Инициализируем push-уведомления
          initializeNotifications().catch(err => 
            console.error('Ошибка инициализации уведомлений:', err)
          );
        }
      }
    } catch (error) {
      console.error('Ошибка при загрузке токена из localStorage:', error);
    }
  }, []);

  const login = async (email: string, password: string) => {
    const data = await authAPI.login(email, password);
    setToken(data.token);
    setUser(data.user);
    try {
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      // Проверяем, что токен действительно сохранился (особенно важно в Electron)
      const savedToken = localStorage.getItem('token');
      if (savedToken !== data.token) {
        console.error('Предупреждение: токен не сохранился правильно в localStorage');
      }
    } catch (error) {
      console.error('Ошибка при сохранении токена в localStorage:', error);
      // Пробуем sessionStorage как fallback
      try {
        sessionStorage.setItem('token', data.token);
        sessionStorage.setItem('user', JSON.stringify(data.user));
      } catch (e) {
        console.error('Ошибка при сохранении токена в sessionStorage:', e);
      }
    }
    
    // Инициализируем push-уведомления после входа
    initializeNotifications().catch(err => 
      console.error('Ошибка инициализации уведомлений:', err)
    );
  };

  const register = async (username: string, email: string, password: string) => {
    const data = await authAPI.register(username, email, password);
    // Registration doesn't return token - user needs admin approval
    if (data.token) {
      setToken(data.token);
      setUser(data.user);
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
    }
    // Return message for UI
    return data;
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setOriginalAdmin(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('originalAdmin');
  };

  const logoutFromImpersonation = async () => {
    // Return to original admin account
    if (originalAdmin) {
      try {
        const { adminAPI } = await import('../api/api');
        const data = await adminAPI.returnFromImpersonation();
        setToken(data.token);
        setUser(data.user);
        setOriginalAdmin(null);
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.removeItem('originalAdmin');
        // Перезагружаем страницу для обновления всех данных компонентов
        window.location.reload();
      } catch (error) {
        console.error('Ошибка при возврате к админу:', error);
        logout();
      }
    } else {
      logout();
    }
  };

  const impersonateUser = async (userId: number) => {
    const { adminAPI } = await import('../api/api');
    const data = await adminAPI.impersonateUser(userId);
    setToken(data.token);
    setUser(data.user);
    setOriginalAdmin(data.originalAdmin);
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    localStorage.setItem('originalAdmin', JSON.stringify(data.originalAdmin));
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        originalAdmin,
        login,
        register,
        logout,
        logoutFromImpersonation,
        impersonateUser,
        isAuthenticated: !!token,
        isAdmin: user?.role === 'admin' || !!user?.is_manager,
        isManager: !!user?.is_manager,
        isImpersonating: !!originalAdmin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

